<template>
  <main class="grow tpl-hub">
    <section class="tpl-hub-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <h1>Free templates for client work.</h1>
        <p class="tpl-hub-lede">
          Proposals and client documents you can download as PDF or Word, edit and send. Free, with no
          email needed. Each one comes with short guidance and a filled-in example.
        </p>
      </div>
    </section>

    <section class="tpl-hub-list">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16">
        <h2>Download a template</h2>
        <div class="tpl-hub-grid">
          <NuxtLink
            v-for="template in freeTemplates"
            :key="template.slug"
            :to="localePath(`/templates/${template.slug}`)"
            class="tpl-hub-card"
          >
            <img
              :src="template.preview.src.replace(/\.webp$/, '-320.webp')"
              :width="template.preview.width"
              :height="template.preview.height"
              alt=""
              loading="lazy"
              decoding="async"
            >
            <div>
              <h3>{{ template.cardTitle }}</h3>
              <p>{{ template.cardCopy }}</p>
              <span class="tpl-hub-more">Get the template <i aria-hidden="true">-&gt;</i></span>
            </div>
          </NuxtLink>
        </div>
        <p class="tpl-hub-note">
          Prefer to fill something in on screen? Try the
          <NuxtLink :to="localePath('/tools/invoice-template')">invoice template</NuxtLink> and
          <NuxtLink :to="localePath('/tools/freelance-contract-template')">freelance contract template</NuxtLink>
          builders, or browse all <NuxtLink :to="localePath('/tools')">free tools</NuxtLink>.
        </p>
      </div>
    </section>
  </main>
</template>

<script setup>
import { localizedUrl } from '~/data/locales';
import { freeTemplates } from '~/data/free-templates';

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() => localizedUrl(siteUrl, '/templates', canonicalLocaleCode.value));
const title = 'Free Templates for Freelancers and Agencies (PDF & Word)';
const description =
  'Free proposal and client-work templates for freelancers, consultants and small agencies. Download as PDF or Word, no email needed, with a filled-in example.';

useHead(() => ({
  title,
  meta: [
    { name: 'description', content: description },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: 'templates-itemlist-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Free templates',
        itemListElement: freeTemplates.map((template, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: template.cardTitle,
          url: localizedUrl(siteUrl, `/templates/${template.slug}`, canonicalLocaleCode.value),
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.tpl-hub-hero {
  background:
    radial-gradient(circle at 50% 0%, rgb(27 167 206 / 0.13), transparent 30rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 8.5rem 0 3.5rem;
}

.dark .tpl-hub-hero {
  background:
    radial-gradient(circle at 12% 12%, rgb(27 167 206 / 0.18), transparent 28rem),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.tpl-hub-hero h1 {
  max-width: 44rem;
  color: #020617;
  font-size: clamp(2.25rem, 5vw, 3.9rem);
  font-weight: 900;
  line-height: 1.06;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.dark .tpl-hub-hero h1 {
  color: #ffffff;
}

.tpl-hub-lede {
  margin-top: 1.25rem;
  max-width: 40rem;
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.75;
}

.dark .tpl-hub-lede {
  color: #cbd5e1;
}

.tpl-hub-list {
  border-top: 1px solid #dbe4ee;
  background: #ffffff;
}

.dark .tpl-hub-list {
  border-color: #243247;
  background: #020617;
}

.tpl-hub-list h2 {
  color: #0f172a;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 900;
}

.dark .tpl-hub-list h2 {
  color: #f8fafc;
}

.tpl-hub-grid {
  display: grid;
  gap: 1.25rem;
  margin-top: 1.75rem;
}

.tpl-hub-card {
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 1.1rem;
  text-decoration: none;
  transition: border-color 150ms ease;
}

.dark .tpl-hub-card {
  border-color: #243247;
  background: #0f172a;
}

.tpl-hub-card:hover {
  border-color: rgb(27 167 206 / 0.5);
}

.tpl-hub-card img {
  width: 100%;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
}

.dark .tpl-hub-card img {
  border-color: #334155;
}

.tpl-hub-card h3 {
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 850;
}

.dark .tpl-hub-card h3 {
  color: #f1f5f9;
}

.tpl-hub-card p {
  margin-top: 0.4rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.6;
}

.dark .tpl-hub-card p {
  color: #94a3b8;
}

.tpl-hub-more {
  display: inline-block;
  margin-top: 0.7rem;
  color: #0e7490;
  font-size: 0.88rem;
  font-weight: 800;
}

.dark .tpl-hub-more {
  color: #67e8f9;
}

.tpl-hub-more i {
  font-style: normal;
}

.tpl-hub-note {
  margin-top: 2rem;
  max-width: 44rem;
  color: #475569;
  font-size: 0.93rem;
  line-height: 1.7;
}

.dark .tpl-hub-note {
  color: #94a3b8;
}

.tpl-hub-note a {
  color: #0e7490;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.dark .tpl-hub-note a {
  color: #67e8f9;
}

@media (min-width: 768px) {
  .tpl-hub-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .tpl-hub-hero {
    padding: 7rem 0 2.75rem;
  }
}

@media (max-width: 400px) {
  .tpl-hub-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .tpl-hub-card img {
    max-width: 6.5rem;
  }

  .tpl-hub-card h3,
  .tpl-hub-card p {
    overflow-wrap: anywhere;
  }
}
</style>
